import { useState } from 'react'
import { parseCell, parseScore, type SheetInfo } from '@concours/shared'
import { Alert, Button, Modal, Textarea } from '@concours/ui'
import type { Draft } from './SheetPage'

interface Line {
  line: number
  id: string
  raw: string
  rowId: string | null
  draft: Draft | null
  error: string | null
}

/**
 * Import de notes (CSV / copier-coller d'un tableur) : « identifiant;note » par
 * ligne, identifiant = code d'anonymat ou matricule de candidature ; « ABS »,
 * « DISP » acceptés. Prévisualisation avec rapport d'erreurs ; rien n'est
 * enregistré avant « Enregistrer » (mêmes contrôles que la saisie).
 */
export function ImportModal({
  sheet,
  kind,
  onClose,
  onApply,
}: {
  sheet: SheetInfo
  kind: 'numeric' | 'performance' | 'status'
  onClose: () => void
  onApply: (drafts: Record<string, Draft>) => void
}) {
  const [text, setText] = useState('')
  const statuses = sheet.discipline?.statuses ?? sheet.stage.statuses
  const byLabel = new Map(sheet.rows.map((r) => [r.label.toUpperCase(), r.stageCandidateId]))

  const lines: Line[] = text
    .split(/\r?\n/)
    .map((l, i) => ({ l: l.trim(), i }))
    .filter(({ l }) => l && !/^(code|matricule|identifiant)\b/i.test(l))
    .map(({ l, i }) => {
      const [id = '', raw = ''] = l.split(/[;\t,]/).map((x) => x.trim())
      const rowId = byLabel.get(id.toUpperCase()) ?? null
      let draft: Draft | null = null
      let error: string | null = rowId ? null : 'Identifiant inconnu dans ce lot.'
      if (rowId) {
        if (kind === 'status') {
          const cell = parseCell(raw)
          const s = statuses.find(
            (x) => x.code === raw.toLowerCase() || x.label.toLowerCase() === raw.toLowerCase()
          )
          if (cell.status !== 'present') draft = { status: cell.status, text: '', code: '' }
          else if (s) draft = { status: 'present', text: '', code: s.code }
          else error = 'Statut inconnu.'
        } else {
          const cell = parseCell(raw)
          if (cell.status !== 'present') draft = { status: cell.status, text: '', code: '' }
          else if (parseScore(raw) === null || Number.isNaN(parseScore(raw)))
            error = 'Valeur non reconnue.'
          else draft = { status: 'present', text: raw.replace('.', ','), code: '' }
        }
      }
      return { line: i + 1, id, raw, rowId, draft, error }
    })
  const ok = lines.filter((l) => l.draft && l.rowId)
  const bad = lines.filter((l) => l.error)

  const readFile = async (file: File | undefined) => {
    if (file) setText(await file.text())
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title="Importer des notes"
      description="Une ligne par candidat : identifiant ; note (ou ABS, DISP). L’identifiant est le code affiché dans la première colonne du relevé."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={!ok.length}
            onClick={() => onApply(Object.fromEntries(ok.map((l) => [l.rowId!, l.draft!])))}
          >
            Reprendre {ok.length} ligne(s)
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        <label className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }}>
          Choisir un fichier CSV
          <input
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="sr-only"
            onChange={(e) => void readFile(e.target.files?.[0])}
          />
        </label>
        <Textarea
          label="Contenu"
          rows={8}
          value={text}
          placeholder={`${sheet.rows[0]?.label ?? 'CODE'};12,5\n${sheet.rows[1]?.label ?? 'CODE'};ABS`}
          onChange={(e) => setText(e.target.value)}
        />
        {lines.length > 0 && (
          <Alert tone={bad.length ? 'warning' : 'success'}>
            {ok.length} ligne(s) reconnue(s)
            {bad.length ? `, ${bad.length} en erreur (ignorée(s)) :` : '.'}
            {bad.length > 0 && (
              <ul className="sel-issues">
                {bad.slice(0, 20).map((l) => (
                  <li key={l.line}>
                    Ligne {l.line} ({l.id || '—'}) : {l.error}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </div>
    </Modal>
  )
}
